/**
 * ID generation utilities for FleetSkipper
 * Provides consistent ID formats across the application
 */

/**
 * Generate a UUID v4
 * Future: Can be replaced with server-generated IDs
 */
export const genId = () => {
  // Simple UUID v4 generation
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

/**
 * Generate a short ID (8 characters)
 * Useful for user-facing IDs
 */
export const genShortId = () => {
  return Math.random().toString(36).substring(2, 10).toUpperCase();
};

/**
 * Generate an ISO timestamp
 */
export const genTimestamp = () => {
  return new Date().toISOString();
};

/**
 * Generate a vessel ID
 * Format: VSL[timestamp]-[year]
 */
export const genVesselId = () => {
  const timestamp = Date.now().toString().slice(-6);
  const year = new Date().getFullYear().toString().slice(-2);
  return `VSL${timestamp}-${year}`;
};

/**
 * Generate a document ID
 * Format: DOC-[type]-[timestamp]
 */
export const genDocId = (type = 'GEN') => {
  const timestamp = Date.now().toString().slice(-8);
  return `DOC-${type.toUpperCase()}-${timestamp}`;
};

/**
 * Generate a review ID
 * Format: REV-[year]-[sequence]
 */
export const genReviewId = () => {
  const year = new Date().getFullYear();
  const sequence = Math.floor(Math.random() * 9999).toString().padStart(4, '0');
  return `REV-${year}-${sequence}`;
};

/**
 * Generate a report ID
 * Format: RPT-[type]-[date]-[sequence]
 */
export const genReportId = (type = 'GEN') => {
  const date = new Date().toISOString().split('T')[0].replace(/-/g, '');
  const sequence = Math.floor(Math.random() * 999).toString().padStart(3, '0');
  return `RPT-${type.toUpperCase()}-${date}-${sequence}`;
};

/**
 * Generate a policy ID
 * Format: POL-[sequence]
 */
export const genPolicyId = () => {
  const sequence = Math.floor(Math.random() * 99999).toString().padStart(5, '0');
  return `POL-${sequence}`;
};

/**
 * Check if an ID is valid UUID
 */
export const isValidUUID = (id) => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(id);
};

/**
 * Parse a compound ID to extract components
 * Example: VSL001-25 -> { prefix: 'VSL', number: '001', year: '25' }
 */
export const parseId = (id) => {
  const parts = id.split('-');
  if (parts.length < 2) return { raw: id };
  
  const prefix = parts[0].replace(/[0-9]/g, '');
  const number = parts[0].replace(/[A-Z]/gi, '');
  const suffix = parts.slice(1).join('-');
  
  return {
    raw: id,
    prefix,
    number,
    suffix
  };
};